import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, ArrowRightLeft, Inbox } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/dentlab/src/components/ui/dialog";
import { Button as ShadcnButton } from "@/dentlab/src/components/ui/button";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { JobWithShare, PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { JobDocumentTags } from "@/dentlab/src/pages/job-page/job-document/job-document-tag.component";
import { useJobSheet } from "@/dentlab/src/store/Sheets/sheet-hooks/useJobSheet";

const formatDateTime = (
	dateStr: string | null | undefined,
	timeStr?: string | null | undefined
) => {
	if (!dateStr) return "–";
	const date = new Date(dateStr).toLocaleDateString("de-CH", {
		day: "2-digit",
		month: "2-digit",
		year: "2-digit",
	});
	if (!timeStr) return date;
	return `${date} ${timeStr.slice(0, 5)}`;
};

const DetailField: React.FC<{
	label: string;
	value: React.ReactNode;
	colSpan?: 1 | 2;
}> = ({ label, value, colSpan = 1 }) => (
	<div
		className={`flex items-baseline gap-1.5 min-w-0 ${
			colSpan === 2 ? "col-span-2" : ""
		}`}
	>
		<span className="text-[11px] uppercase tracking-wide text-gray-400 shrink-0">
			{label}
		</span>
		<span className="text-sm text-gray-800 break-words min-w-0">
			{value}
		</span>
	</div>
);

export const InProgressJobsSuggestionDialog: React.FC = () => {
	const navigate = useNavigate();
	const { openJobSheet } = useJobSheet();
	const {
		inProgressJobSuggestions,
		dismissInProgressJobSuggestions,
		hardDeleteJob,
		shareFile,
		unshareFile,
		acceptJobRequest,
		denyJobRequest,
		job,
	} = useJobStore((state) => ({
		inProgressJobSuggestions: state.inProgressJobSuggestions,
		dismissInProgressJobSuggestions: state.dismissInProgressJobSuggestions,
		hardDeleteJob: state.hardDeleteJob,
		shareFile: state.shareFile,
		unshareFile: state.unshareFile,
		acceptJobRequest: state.acceptJobRequest,
		denyJobRequest: state.denyJobRequest,
		job: state.job,
	}));
	const patient = usePatientStore((state) =>
		job?.patient_id ? state.patientsLookup[job.patient_id] : null
	);
	const { clientsLookup, practitionersLookup } = useCentralStore((state) => ({
		clientsLookup: state.clientsLookup,
		practitionersLookup: state.practitionersLookup,
	}));

	const [pendingActionJobId, setPendingActionJobId] = useState<number | null>(
		null
	);

	const open =
		!!inProgressJobSuggestions && inProgressJobSuggestions.length > 0;

	if (!open) return null;

	const suggestions = inProgressJobSuggestions!;
	const currentJobId = job?.id ?? null;
	const patientName = patient
		? [
				(patient as PatientEntityType).first_name,
				(patient as PatientEntityType).last_name,
			]
				.filter(Boolean)
				.join(" ")
		: "";
	const patientLabel = patientName || "dieser Patient";

	const handleLook = (suggested: JobWithShare) => {
		openJobSheet({
			job: suggested,
			onShareFile: shareFile,
			onUnshareFile: unshareFile,
			onAcceptJobRequest: acceptJobRequest,
			onDenyJobRequest: denyJobRequest,
		});
	};

	const handleSwitchAndDelete = async (suggested: JobWithShare) => {
		if (!suggested.client_id || !suggested.id || !currentJobId) return;
		setPendingActionJobId(suggested.id);
		const deleted = await hardDeleteJob(currentJobId, false);
		setPendingActionJobId(null);
		if (!deleted) return;
		dismissInProgressJobSuggestions();
		navigate(`/client/${suggested.client_id}/job/${suggested.id}`);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next && pendingActionJobId === null)
					dismissInProgressJobSuggestions();
			}}
		>
			<DialogContent
				data-testid="in-progress-jobs-suggestion-dialog"
				className="max-w-2xl gap-3"
			>
				<DialogHeader className="space-y-0.5">
					<DialogTitle>Offene Aufträge gefunden</DialogTitle>
					<DialogDescription>
						Für <b>{patientLabel}</b>{" "}
						{suggestions.length === 1
							? "existiert bereits ein offener Auftrag"
							: `existieren bereits ${suggestions.length} offene Aufträge`}
						.
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto pr-1">
					{suggestions.map((s) => {
						const client = s.client_id
							? clientsLookup[s.client_id]
							: null;
						const clientName = client
							? `${client.first_name ?? ""} ${
									client.last_name ?? ""
								}`.trim()
							: "";
						const prac = s.practitioner_id
							? practitionersLookup[s.practitioner_id]
							: null;
						const practitionerName = prac
							? `${prac.first_name ?? ""} ${
									prac.last_name ?? ""
								}`.trim()
							: "";
						const fromRequest = !!s.channel_id;
						const isPending = pendingActionJobId === s.id;

						return (
							<div
								key={s.id}
								data-testid={`in-progress-jobs-suggestion-item-${s.id}`}
								className="rounded-lg border bg-white px-3 py-2 flex gap-3"
							>
								<div className="flex-1 min-w-0 flex flex-col gap-1.5">
									<div className="flex items-baseline gap-2 flex-wrap">
										<span className="text-base font-semibold tabular-nums">
											{s.code ?? `#${s.id}`}
										</span>
										{s.title && (
											<span className="text-base text-gray-800">
												{s.title}
											</span>
										)}
									</div>
									<div className="grid grid-cols-2 gap-x-6 gap-y-1">
										<DetailField
											label="Kunde"
											value={clientName || "–"}
										/>
										<DetailField
											label="Eingang"
											value={formatDateTime(
												s.incoming_order_date,
												s.incoming_order_time
											)}
										/>
										<DetailField
											label="Behandler"
											value={practitionerName || "–"}
										/>
										<DetailField
											label="Ausgang"
											value={formatDateTime(
												s.outgoing_order_date,
												s.outgoing_order_time
											)}
										/>
										<DetailField
											label="Farbe"
											value={s.tooth_color || "–"}
										/>
										<DetailField
											label="Total"
											value={
												s.kv_total_final != null &&
												s.kv_total_final > 0
													? `CHF ${s.kv_total_final.toFixed(
															2
														)}`
													: "–"
											}
										/>
										{s.description && (
											<DetailField
												label="Beschr."
												colSpan={2}
												value={
													<span className="whitespace-pre-wrap">
														{s.description}
													</span>
												}
											/>
										)}
									</div>
								</div>
								<div className="shrink-0 flex flex-col items-end justify-between gap-3">
									<div className="flex items-center gap-1.5 flex-wrap justify-end">
										<JobDocumentTags job={s} />
										{fromRequest ? (
											<span
												className="inline-flex items-center gap-1 rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[11px] font-medium"
												title="Aus Auftragsanfrage entstanden"
											>
												<Inbox className="h-3 w-3" />
												Aus Anfrage
											</span>
										) : null}
									</div>
									<div className="flex flex-col items-end gap-1.5">
										<ShadcnButton
											variant="outline"
											size="sm"
											onClick={() => handleLook(s)}
											disabled={isPending}
											aria-label={`Auftrag ${
												s.code ?? s.id
											} im Sheet ansehen`}
											data-testid={`in-progress-jobs-suggestion-look-${s.id}`}
											className="h-7 gap-1 px-2 text-xs justify-start"
										>
											<Eye className="h-3.5 w-3.5" />
											Ansehen
										</ShadcnButton>
										<ShadcnButton
											size="sm"
											onClick={() =>
												handleSwitchAndDelete(s)
											}
											disabled={
												isPending || !currentJobId
											}
											aria-label={`Auftrag ${
												s.code ?? s.id
											} übernehmen und aktuellen löschen`}
											data-testid={`in-progress-jobs-suggestion-switch-${s.id}`}
											className="h-7 gap-1 px-2 text-xs justify-start"
										>
											<ArrowRightLeft className="h-3.5 w-3.5" />
											{isPending
												? "Wird übernommen…"
												: "Übernehmen & löschen"}
										</ShadcnButton>
									</div>
								</div>
							</div>
						);
					})}
				</div>
				<div className="flex items-center justify-center gap-2">
					<ShadcnButton
						size="sm"
						onClick={dismissInProgressJobSuggestions}
						disabled={pendingActionJobId !== null}
						aria-label="Mit neu erstelltem Auftrag fortfahren"
						data-testid="in-progress-jobs-suggestion-dismiss"
						className="px-6"
					>
						Mit neu erstelltem Auftrag fortfahren
					</ShadcnButton>
					<span className="text-[11px] text-gray-400">
						oder{" "}
						<kbd className="px-1 py-0.5 rounded border bg-gray-50 text-gray-500 font-mono text-[10px]">
							Esc
						</kbd>
					</span>
				</div>
			</DialogContent>
		</Dialog>
	);
};
